import i18n from "i18next";
import { initReactI18next } from "react-i18next";
import LanguageDetector from "i18next-browser-languagedetector";

import enCommon from "./locales/en/common.json";
import enSmartImport from "./locales/en/smartImport.json";
import enImports from "./locales/en/imports.json";
import enPim from "./locales/en/pim.json";

import deCommon from "./locales/de/common.json";
import deSmartImport from "./locales/de/smartImport.json";
import deImports from "./locales/de/imports.json";
import dePim from "./locales/de/pim.json";

const enableDebug = import.meta.env.VITE_IS_DEBUG;

i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    debug: enableDebug,
    fallbackLng: "en",
    defaultNS: "common",
    interpolation: {
      escapeValue: false,
    },
    react: {
      wait: false,
      withRef: false,
      bindI18n: "languageChanged loaded",
      bindStore: "added removed",
      nsMode: "default",
    },
    resources: {
      en: {
        common: enCommon,
        smartImport: enSmartImport,
        imports: enImports,
        pim: enPim,
        // Kept inline for Boilerplate component compat
        ImportStatus: {
          pending: "Pending",
          processing: "Processing",
          unmapped: "Unmapped",
          mapped: "Mapped",
          succeeded: "Succeeded",
          completedWithErrors: "Completed with errors",
          failed: "Failed",
          unknown: "Unknown",
        },
        FIDO: {
          fidoKey: {
            registeredSuccess: "Passkey registered successfully",
            keyAdded: "Your passkey has been added",
            fetchError: "Error fetching passkeys",
            deletedSuccess: "Passkey deleted successfully",
            keyRemoved: "Passkey removed",
            deleteError: "Error deleting passkey",
            lastUsed: "Last used",
            created: "created",
            actions: {
              add: "Add passkey",
              remove: "Remove passkey",
            },
            title_passkeys: "Passkeys",
          },
          registerError: "Error registering passkey",
        },
        Auth: {
          name_placeholder: "John Doe",
          email_placeholder: "doe@example.com",
          register: "Register",
          name: "Name",
          current_password: "Current password",
          new_password: "New password",
          change_password: "Change password",
          title: "Login to FacetFlux",
          invalid_email: "Invalid email",
          invalid_password: "Password should include at least 8 characters",
          login_failed: "Login failed",
          incorrect_user_or_password: "Incorrect user or password",
          rate_limit_exceeded:
            "Too many login attempts. Please try again later.",
          unexpected_error: "An unexpected error has occurred: ",
          email: "Email",
          password: "Password",
          your_password: "Your password",
          forgot_password: "Forgot password?",
          login: "Login",
          you_are_logged_in: "You are logged in",
          password_reset_requested: "Password reset requested",
          password_reset_requested_message:
            "Please check your email and follow the reset link to set a new password.",
          recover_password: "Recover password",
          request_reset_email: "Send recovery email",
          login_instead: "Login instead",
        },
        Users: {
          add_user: "Add user",
          email: "Email",
          first_name: "First name",
          last_name: "Last name",
          role: "Role",
          created: "Created",
          is_active: "Active",
          user_deleted: "User deleted!",
          invite_user: "Invite user",
          invitation_sent: "Invitation sent!",
          invite_user_info:
            "This will send an invitation to the user so they can join you to collaborate. Users can choose to login via a different login than email, but an email is required for the invitation.",
          save: "Save",
        },
        // Legacy namespace — common.json now carries these keys too,
        // but existing Boilerplate components still reference "translation:..."
        translation: enCommon,
      },
      de: {
        common: deCommon,
        smartImport: deSmartImport,
        imports: deImports,
        pim: dePim,
        ImportStatus: {
          pending: "Ausstehend",
          processing: "Wird verarbeitet",
          unmapped: "Nicht zugeordnet",
          mapped: "Zugeordnet",
          succeeded: "Erfolgreich",
          completedWithErrors: "Mit Fehlern abgeschlossen",
          failed: "Fehlgeschlagen",
          unknown: "Unbekannt",
        },
        FIDO: {
          fidoKey: {
            registeredSuccess: "Passkey erfolgreich registriert",
            keyAdded: "Ihr Passkey wurde hinzugefügt",
            fetchError: "Fehler beim Abrufen der Passkeys",
            deletedSuccess: "Passkey erfolgreich gelöscht",
            keyRemoved: "Passkey entfernt",
            deleteError: "Fehler beim Löschen des Passkeys",
            lastUsed: "Zuletzt verwendet",
            created: "erstellt",
            actions: {
              add: "Passkey hinzufügen",
              remove: "Passkey entfernen",
            },
            title_passkeys: "Passkeys",
          },
          registerError: "Fehler bei der Passkey-Registrierung",
        },
        Auth: {
          name_placeholder: "Max Mustermann",
          email_placeholder: "max@beispiel.de",
          register: "Registrieren",
          name: "Name",
          current_password: "Aktuelles Passwort",
          new_password: "Neues Passwort",
          change_password: "Passwort ändern",
          title: "Bei FacetFlux anmelden",
          invalid_email: "Ungültige E-Mail-Adresse",
          invalid_password: "Das Passwort muss mindestens 8 Zeichen enthalten",
          login_failed: "Anmeldung fehlgeschlagen",
          incorrect_user_or_password: "Falscher Benutzername oder falsches Passwort",
          rate_limit_exceeded:
            "Zu viele Anmeldeversuche. Bitte versuchen Sie es später erneut.",
          unexpected_error: "Ein unerwarteter Fehler ist aufgetreten: ",
          email: "E-Mail",
          password: "Passwort",
          your_password: "Ihr Passwort",
          forgot_password: "Passwort vergessen?",
          login: "Anmelden",
          you_are_logged_in: "Sie sind angemeldet",
          password_reset_requested: "Passwort-Zurücksetzung angefordert",
          password_reset_requested_message:
            "Bitte überprüfen Sie Ihre E-Mails und folgen Sie dem Link zum Zurücksetzen Ihres Passworts.",
          recover_password: "Passwort wiederherstellen",
          request_reset_email: "Wiederherstellungs-E-Mail senden",
          login_instead: "Stattdessen anmelden",
        },
        Users: {
          add_user: "Benutzer hinzufügen",
          email: "E-Mail",
          first_name: "Vorname",
          last_name: "Nachname",
          role: "Rolle",
          created: "Erstellt",
          is_active: "Aktiv",
          user_deleted: "Benutzer gelöscht!",
          invite_user: "Benutzer einladen",
          invitation_sent: "Einladung gesendet!",
          invite_user_info:
            "Hiermit wird eine Einladung an den Benutzer gesendet, damit er mit Ihnen zusammenarbeiten kann. Benutzer können sich auch über andere Methoden als E-Mail anmelden, aber eine E-Mail-Adresse ist für die Einladung erforderlich.",
          save: "Speichern",
        },
        translation: deCommon,
      },
    },
  });

export default i18n;
